import React, { useEffect, useRef } from "react";
import { getClaimsBFFVersion } from "../utils/api/core";
import { useToast, Box, Button, HStack, IconButton, Text } from "@chakra-ui/react";
import { CloseIcon } from "@chakra-ui/icons";
import { TbRefreshAlert } from "react-icons/tb";

const { version: appVersion } = require("../../package.json");

export function useRefreshCheck() {
	const toast = useToast();
	const intervalMs = 1000 * 60 * 30; // 30 minutes
	const toastIdRef = useRef<string | number | null>(null);
	const intervalRef = useRef<NodeJS.Timeout | null>(null);

	const showRefreshToast = () => {
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		if (toastIdRef.current && typeof toast.isActive === "function" && toast.isActive(toastIdRef.current)) return;

		toastIdRef.current = toast({
			position: "bottom-left",
			duration: null,
			render: ({ onClose }) =>
				// Build the toast content without JSX so file can remain .ts
				React.createElement(
					Box,
					{ m: 3, color: "white", p: 3, bg: "orange.500", borderRadius: "8px", textAlign: "center" },
					React.createElement(
						HStack,
						{ spacing: 3 },
						React.createElement(TbRefreshAlert, { fontSize: "28px" }),
						React.createElement(
							Text,
							{ flex: "1", style: { marginBottom: "6px" } },
							"There is a new update available. Please refresh your page."
						),
						React.createElement(IconButton, {
							ml: 2,
							size: "sm",
							variant: "ghost",
							colorScheme: "whiteAlpha",
							"aria-label": "Close",
							icon: React.createElement(CloseIcon, { fontSize: "14px" }),
							onClick: () => {
								onClose();
							}
						})
					),
					React.createElement(
						Button,
						{
							mt: 3,
							bg: "rgba(255,255,255,0.30)",
							colorScheme: "whiteAlpha",
							_hover: { bg: "rgba(255,255,255,0.20)" },
							_focus: { bg: "rgba(255,255,255,0.20)" },
							onClick: () => window.location.reload()
						},
						"Refresh Now"
					)
				)
		});
	};

	const checkVersion = async () => {
		try {
			const response = await getClaimsBFFVersion();
			const bffVersion = response?.getVersion?.() ?? "";
			if (bffVersion && bffVersion !== appVersion) {
				// Stop further polling once we detected a mismatch and notify user
				if (intervalRef.current) {
					clearInterval(intervalRef.current);
					intervalRef.current = null;
				}
				showRefreshToast();
			}
		} catch (error) {
			// Silent fail — background check not critical
			console.error("Error checking app version:", error);
		}
	};

	useEffect(() => {
		// initial check
		checkVersion();

		// schedule interval
		intervalRef.current = setInterval(checkVersion, intervalMs);

		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
				intervalRef.current = null;
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [appVersion]);

	return null;
}

export default useRefreshCheck;
